import { useRef } from 'react';
import styled from 'styled-components';
import { TextAreaField } from 'lib/iterable-hook-form';
import { useFormContext } from 'react-hook-form';
import { SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { AiSparkle24 } from 'ui/icons';
import { SmallSegmentedControl } from 'ui/segmentedControl';
import { Caption, colors, easing, duration } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useJourneyAssistContext } from 'modules/workflows/context/JourneyAssistContext';
import {
  PROMPT_DEFAULT,
  PROMPT_HELPERS,
  PROMPT_PRESETS,
  PROMPT_TEXT,
  PROMPT_FIELD,
  PROMPT_MAX_CHAR_COUNT,
  PromptType,
  ToggleType,
  TOGGLE_OPTIONS,
} from './JourneyAssist.constants';
import { HelperPills } from './HelperPills';

const HEIGHT_WITH_CONTROLS = 127;
const HEIGHT = 141;
const PILLS_HEIGHT = 84;
const MAX_HEIGHT = 354;
const MAX_WIDTH = 308;

const PromptField = styled.div<{ arePillsShown?: boolean }>`
  position: relative;

  > div:first-child {
    transition-property: padding;
    transition-duration: ${duration.long};
    transition-timing-function: ${easing.standard};
    transition-delay: ${({ arePillsShown }) => (arePillsShown ? 0 : duration.long)};
    padding-bottom: ${({ arePillsShown }) => (arePillsShown ? pxToRem(PILLS_HEIGHT) : 0)};
    max-height: ${pxToRem(MAX_HEIGHT)};

    > div::after {
      padding: ${pxToRem(8)};
      max-width: ${pxToRem(MAX_WIDTH)};
    }

    textarea {
      max-width: ${pxToRem(MAX_WIDTH)};
      max-height: ${({ arePillsShown }) =>
        arePillsShown ? pxToRem(MAX_HEIGHT - PILLS_HEIGHT) : pxToRem(MAX_HEIGHT)};
      overflow-y: auto;
    }
  }
`;

const CharacterCount = styled(Caption)`
  color: ${colors.textSecondary};
  line-height: 0.5;
`;

const StyledButton = styled(SecondaryButton)`
  svg path {
    stroke: none !important;
  }
`;

export const GENERATE_BUTTON_TEST_ID = 'generate-button';
export const PROMPT_TEXTAREA_TEST_ID = 'prompt-textarea';
export const DIALOG_CLOSE_BUTTON_TEST_ID = 'journey-assist-close-button';

type PromptEditProps = {
  onFetchAIGeneratedTiles: () => void;
  selectedAINodes?: number;
  setAddOrReplaceToggle?: (value: ToggleType) => void;
  addOrReplace?: string;
  showToggle?: boolean;
  isEdit: boolean;
};

export const PromptEdit = ({
  onFetchAIGeneratedTiles,
  selectedAINodes,
  setAddOrReplaceToggle,
  addOrReplace,
  showToggle,
  isEdit,
}: PromptEditProps) => {
  const { promptType } = useJourneyAssistContext();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  /** React hook form */
  const { watch } = useFormContext();
  const [prompt] = watch([PROMPT_FIELD]);

  const isNewJourney = promptType === PromptType.NewJourney;
  const shouldShowDefaultPresets = prompt.length <= PROMPT_DEFAULT.length && isNewJourney;
  const shouldShowHelperPills = prompt.length < PROMPT_MAX_CHAR_COUNT && !shouldShowDefaultPresets;

  const helpers = isNewJourney
    ? PROMPT_HELPERS
    : PROMPT_HELPERS.filter((p) => !p.onlyShowInNewJourneys);

  return (
    <>
      {showToggle && isEdit && (
        <SmallSegmentedControl
          isFullWidth
          onSelect={setAddOrReplaceToggle}
          value={addOrReplace}
          options={TOGGLE_OPTIONS}
        />
      )}
      <PromptField arePillsShown={shouldShowDefaultPresets || shouldShowHelperPills}>
        <TextAreaField
          id="journey-assist-prompt-input"
          name={PROMPT_FIELD}
          data-test={PROMPT_TEXTAREA_TEST_ID}
          maxLength={PROMPT_MAX_CHAR_COUNT}
          autoFocus
          inputRef={inputRef}
          placeholder={promptType ? PROMPT_TEXT[promptType].placeholder : ''}
          autoGrowMinHeight={showToggle ? HEIGHT_WITH_CONTROLS : HEIGHT}
          isDisabled={!isEdit}
        />
        {isEdit && (
          <>
            <HelperPills
              inputRef={inputRef}
              pills={PROMPT_PRESETS}
              shouldShow={shouldShowDefaultPresets}
            />
            <HelperPills
              inputRef={inputRef}
              pills={helpers}
              shouldShow={shouldShowHelperPills}
              shouldAddToPrompt
            />
          </>
        )}
      </PromptField>
      {prompt.length >= PROMPT_MAX_CHAR_COUNT - 50 && (
        <CharacterCount>
          {prompt.length}/{PROMPT_MAX_CHAR_COUNT} characters
        </CharacterCount>
      )}
      {isEdit && (
        <StyledButton
          data-test={GENERATE_BUTTON_TEST_ID}
          size={BUTTON_SIZES.LARGE}
          onClick={onFetchAIGeneratedTiles}
        >
          <AiSparkle24 />
          Generate
          {(!!selectedAINodes || addOrReplace === ToggleType.Replace) && ' and replace'}
        </StyledButton>
      )}
    </>
  );
};
